import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { api, APIError } from '../lib/api'
import { useAuth } from '../lib/auth'

export const Route = createFileRoute('/profile')({
  beforeLoad: async () => {
    // Ensure auth is loaded before deciding
    const state = useAuth.getState()
    if (state.loading) {
      await state.load()
    }
    if (!useAuth.getState().user) {
      throw redirect({ to: '/login' })
    }
  },
  component: ProfilePage,
})

function ProfilePage() {
  const user = useAuth((s) => s.user)
  const load = useAuth((s) => s.load)
  const navigate = useNavigate()
  const [currentPwd, setCurrentPwd] = useState('')
  const [newPwd, setNewPwd] = useState('')
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (!user) return null

  async function handleChangePwd(e: React.FormEvent) {
    e.preventDefault()
    setMsg(null)
    setBusy(true)
    try {
      await api('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          current_password: currentPwd,
          new_password: newPwd,
        }),
      })
      setMsg('Пароль обновлён')
      setCurrentPwd('')
      setNewPwd('')
      await load()
    } catch (err) {
      if (err instanceof APIError && err.status === 400) {
        setMsg('Текущий пароль неверный')
      } else {
        setMsg('Не удалось сменить пароль')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto p-8 space-y-6">
      <section className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-6">
        <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">
          {user.full_name}
        </h2>
        <dl className="mt-4 text-sm text-zinc-600 dark:text-zinc-400 grid grid-cols-2 gap-y-2">
          <dt>Email</dt><dd>{user.email}</dd>
          <dt>Роль</dt><dd>{user.role}</dd>
        </dl>
      </section>

      {user.must_change_password && (
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-md p-4 text-amber-800 dark:text-amber-200 text-sm">
          Смените пароль перед использованием системы.
        </div>
      )}

      <section className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-6">
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
          Смена пароля
        </h3>
        <form onSubmit={handleChangePwd} className="space-y-3">
          <input
            type="password"
            placeholder="Текущий пароль"
            value={currentPwd}
            onChange={(e) => setCurrentPwd(e.target.value)}
            required
            className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100"
          />
          <input
            type="password"
            placeholder="Новый пароль (мин. 12 символов)"
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            minLength={12}
            required
            className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100"
          />
          {msg && <p className="text-sm text-zinc-600 dark:text-zinc-400">{msg}</p>}
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-md text-sm"
          >
            {busy ? 'Сохраняю…' : 'Сохранить'}
          </button>
        </form>
      </section>

      {user.role === 'admin' && (
        <section>
          <button
            onClick={() => void navigate({ to: '/admin/users' })}
            className="text-sm text-blue-600 hover:text-blue-700 underline"
          >
            Управление пользователями →
          </button>
        </section>
      )}
    </div>
  )
}
